import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { Shell } from "./Account";
import { useStaticSeo } from "../../hooks/useSeo";
import usePayPal from "../../hooks/usePayPal";
import { currentSession } from "../../data/accountApi";
import { PRODUCT_ORIGIN } from "../../data/brand";
import {
    catalogue as fetchCatalogue,
    createOrder,
    captureOrder,
    activateSubscription,
    me as fetchMe,
} from "../../data/paymentsApi";

/*
 * Buying reports, and buying a plan.
 *
 * TWO PAGES AND NOT ONE, because the PayPal SDK is loaded with a different
 * `intent` for each - "capture" for a one-off, "subscription" for a plan -
 * and the two configurations overwrite each other on window.paypal. A single
 * page with a toggle would need two namespaced SDK loads to avoid that, for
 * the sake of saving a click on a decision people make once. They are also
 * genuinely different decisions: one is "I want this house", the other is "I
 * do this for a living".
 *
 * NO PRICE IS WRITTEN IN THIS FILE. Everything comes from
 * /payments/catalogue, which reads the same PRICING the order is built from
 * and the capture is checked against. A figure typed here would be a fourth
 * copy, and the copy a customer reads is the one that must not be wrong.
 */

const Head = styled.h1`
    margin: 0 0 0.4rem;
    font-size: 1.5rem;
    color: ${(props) => props.theme.darkblue};
`;

const Blurb = styled.p`
    margin: 0 0 1.2rem;
    font-size: 0.95rem;
    line-height: 1.5;
    color: ${(props) => props.theme.grey};
`;

const Options = styled.div`
    display: grid;
    gap: 0.7rem;
    margin-bottom: 1.2rem;
`;

const Option = styled.button`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.6rem;
    width: 100%;
    padding: 0.85rem 1rem;
    text-align: left;
    cursor: pointer;
    border-radius: 8px;
    background: ${(props) => (props.chosen ? props.theme.darkblue : "#fff")};
    color: ${(props) => (props.chosen ? "#fff" : props.theme.black)};
    border: 2px solid
        ${(props) => (props.chosen ? props.theme.darkblue : "rgba(15, 76, 92, 0.25)")};

    strong {
        font-size: 1.05rem;
    }

    span {
        font-size: 0.85rem;
        opacity: 0.85;
    }
`;

const Each = styled.span`
    margin-left: auto;
    white-space: nowrap;

    /*
     * Its own line on a phone. Beside the label it squeezed "Three property
     * reports" into three wrapped lines on a 375px screen, which read as a
     * cramped mistake rather than a saving.
     */
    @media (max-width: 430px) {
        margin-left: 0;
        flex-basis: 100%;
    }
`;

const Buttons = styled.div`
    min-height: 52px;
`;

const Note = styled.p`
    margin: 1rem 0 0;
    font-size: 0.8rem;
    line-height: 1.5;
    color: ${(props) => props.theme.grey};
`;

const Err = styled.p`
    margin: 0.8rem 0 0;
    font-size: 0.9rem;
    color: #b3261e;
`;

const Done = styled.p`
    margin: 0.8rem 0 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: ${(props) => props.theme.darkblue};
`;

const Alt = styled.p`
    margin: 1.2rem 0 0;
    font-size: 0.9rem;

    a {
        color: ${(props) => props.theme.darkblue};
        text-decoration: underline;
    }
`;

/** Everything the catalogue says, fetched once. */
function useCatalogue() {
    const [state, setState] = useState({ loading: true, data: null, error: null });

    useEffect(() => {
        let alive = true;
        fetchCatalogue()
            .then((data) => alive && setState({ loading: false, data: data, error: null }))
            .catch((e) =>
                alive && setState({ loading: false, data: null, error: e.message })
            );
        return () => {
            alive = false;
        };
    }, []);

    return state;
}

/**
 * Sends anybody without a session to sign in, and remembers where they were.
 *
 * A CHECKOUT CANNOT BE ANONYMOUS HERE, because what is bought is attached to
 * an account - credits are spent later, on a property chosen later, possibly
 * on a phone. Taking money with nowhere to put it would be the one failure
 * this whole flow exists to avoid.
 */
function useRequireSession() {
    const history = useHistory();
    const location = useLocation();
    const session = currentSession();

    useEffect(() => {
        if (!session) {
            history.replace(
                "/account/sign-in?next=" + encodeURIComponent(location.pathname)
            );
        }
    }, [session, history, location.pathname]);

    return session;
}

/**
 * Renders PayPal's buttons into a div, once.
 *
 * THE HANDLERS ARE HELD IN A REF AND NOT CLOSED OVER. PayPal renders the
 * buttons into an iframe and keeps whatever callbacks it was given at render
 * time; re-rendering the React component does not replace them. Closing over
 * state directly would leave the buttons calling the first render's
 * functions for ever - which, in a checkout, means capturing against a stale
 * order id.
 */
function PayPalButtons({ ready, sdkError, config, deps }) {
    const host = useRef(null);
    const latest = useRef(config);
    latest.current = config;

    useEffect(() => {
        if (!ready || !host.current || !window.paypal) return undefined;

        host.current.innerHTML = "";
        let buttons;
        try {
            buttons = window.paypal.Buttons({
                style: { shape: "rect", layout: "vertical", label: "pay" },
                createOrder: (...args) =>
                    latest.current.createOrder && latest.current.createOrder(...args),
                createSubscription: (...args) =>
                    latest.current.createSubscription &&
                    latest.current.createSubscription(...args),
                onApprove: (...args) => latest.current.onApprove(...args),
                onCancel: (...args) => latest.current.onCancel && latest.current.onCancel(...args),
                onError: (...args) => latest.current.onError && latest.current.onError(...args),
            });
            buttons.render(host.current);
        } catch (err) {
            /* Rendering failed; the page says so through sdkError above. */
        }

        return () => {
            try {
                if (buttons && buttons.close) buttons.close();
            } catch (err) {
                /* Already gone. */
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, ...(deps || [])]);

    if (sdkError) return <Err>{sdkError}</Err>;
    return <Buttons ref={host} />;
}

/* ---------------------------------------------------------------- reports */

/** One-off reports: one, or three at a discount. */
export function BuyReports() {
    useStaticSeo({
        title: "Buy a property report | Doorstep Data",
        canonical: PRODUCT_ORIGIN + "/account/buy",
        description: "Buy one property report, or three.",
        noindex: true,
    });

    const session = useRequireSession();
    const { loading, data, error } = useCatalogue();
    const [sku, setSku] = useState("single");
    const [failed, setFailed] = useState(null);
    const [bought, setBought] = useState(null);

    const { ready, error: sdkError } = usePayPal(data && data.paypalClientId, "capture");

    const items = ((data && data.items) || []).filter((i) => !i.recurring);

    const onApprove = useCallback(
        async (details) => {
            setFailed(null);
            try {
                const result = await captureOrder(details.orderID, session && session.token);
                setBought(result.credits);
            } catch (e) {
                setFailed(e.message);
            }
        },
        [session]
    );

    if (!session) return null;

    if (bought !== null) {
        return (
            <Shell>
                <Head>Thank you</Head>
                <Done>
                    {bought === 1 ? "You have 1 report" : "You have " + bought + " reports"} to
                    use.
                </Done>
                <Blurb>
                    A report is used the first time you open a property. Opening the same one
                    again is always free, on this website and in the app.
                </Blurb>
                <Alt>
                    <Link to="/property-report">Look a property up</Link> ·{" "}
                    <Link to="/account">Your account</Link>
                </Alt>
            </Shell>
        );
    }

    return (
        <Shell>
            <Head>Buy a property report</Head>
            <Blurb>
                A report covers one address: its council tax band, energy rating, floor area,
                its own sold prices, and an instant valuation. Postcode reports stay free.
            </Blurb>

            {loading && <Blurb>Loading…</Blurb>}
            {error && <Err>{error}</Err>}

            {!loading && !error && (
                <>
                    <Options>
                        {items.map((item) => (
                            <Option
                                key={item.sku}
                                type="button"
                                chosen={sku === item.sku}
                                onClick={() => setSku(item.sku)}
                            >
                                <strong>£{item.amount}</strong>
                                <span>{item.label}</span>
                                {item.credits > 1 && (
                                    <Each>
                                        £{(Number(item.amount) / item.credits).toFixed(2)} each
                                    </Each>
                                )}
                            </Option>
                        ))}
                    </Options>

                    <PayPalButtons
                        ready={ready}
                        sdkError={sdkError}
                        deps={[sku]}
                        config={{
                            createOrder: async () => {
                                setFailed(null);
                                const r = await createOrder(sku, session.token);
                                return r.orderId;
                            },
                            onApprove: onApprove,
                            onError: () =>
                                setFailed("PayPal could not take that payment. Try again."),
                        }}
                    />

                    {failed && <Err>{failed}</Err>}

                    <Note>
                        Reports do not expire, and you choose which property to spend one on when
                        you open it. By paying you agree we may give you the report immediately,
                        so the 14-day right to cancel does not apply once it has been opened.
                    </Note>
                    <Alt>
                        Doing this for a living? <Link to="/account/plan">See the plans</Link>.
                    </Alt>
                </>
            )}
        </Shell>
    );
}

/* ------------------------------------------------------------------ plans */

/** A subscription: ten properties a month, and history kept. */
export function BuyPlan() {
    useStaticSeo({
        title: "Plans | Doorstep Data",
        canonical: PRODUCT_ORIGIN + "/account/plan",
        description: "Ten properties a month, with your history kept.",
        noindex: true,
    });

    const session = useRequireSession();
    const { loading, data, error } = useCatalogue();
    const [sku, setSku] = useState("annual");
    const [failed, setFailed] = useState(null);
    const [started, setStarted] = useState(false);

    const { ready, error: sdkError } = usePayPal(data && data.paypalClientId, "subscription");

    const items = ((data && data.items) || []).filter((i) => i.recurring);
    const chosen = items.find((i) => i.sku === sku) || null;

    const onApprove = useCallback(
        async (details) => {
            setFailed(null);
            try {
                await activateSubscription(details.subscriptionID, session && session.token);
                setStarted(true);
            } catch (e) {
                setFailed(e.message);
            }
        },
        [session]
    );

    if (!session) return null;

    if (started) {
        return (
            <Shell>
                <Head>You are on a plan</Head>
                <Done>Ten properties a month, starting now.</Done>
                <Blurb>
                    Your allowance refreshes when the plan renews. Properties you have already
                    opened stay open for ever, even if you cancel.
                </Blurb>
                <Alt>
                    <Link to="/property-report">Look a property up</Link> ·{" "}
                    <Link to="/account">Your account</Link>
                </Alt>
            </Shell>
        );
    }

    return (
        <Shell>
            <Head>Plans</Head>
            <Blurb>
                Ten properties a month, each with its report and valuation, and your saved
                history kept. Properties you open stay open for ever, plan or no plan.
            </Blurb>

            {loading && <Blurb>Loading…</Blurb>}
            {error && <Err>{error}</Err>}

            {!loading && !error && (
                <>
                    <Options>
                        {items.map((item) => (
                            <Option
                                key={item.sku}
                                type="button"
                                chosen={sku === item.sku}
                                onClick={() => setSku(item.sku)}
                            >
                                <strong>£{item.amount}</strong>
                                <span>{item.label}</span>
                                {item.sku === "annual" && <Each>two months free</Each>}
                            </Option>
                        ))}
                    </Options>

                    {chosen && !chosen.planId && (
                        <Err>Plans are not switched on yet. Buy a report instead.</Err>
                    )}

                    {chosen && chosen.planId && (
                        <PayPalButtons
                            ready={ready}
                            sdkError={sdkError}
                            deps={[sku, chosen.planId]}
                            config={{
                                createSubscription: (dataArg, actions) =>
                                    actions.subscription.create({ plan_id: chosen.planId }),
                                onApprove: onApprove,
                                onError: () =>
                                    setFailed("PayPal could not start that plan. Try again."),
                            }}
                        />
                    )}

                    {failed && <Err>{failed}</Err>}

                    <Note>
                        Cancel whenever you like, from your account. Cancelling stops the next
                        payment and takes nothing away that you have already opened.
                    </Note>
                    <Alt>
                        Only need one? <Link to="/account/buy">Buy a single report</Link>.
                    </Alt>
                </>
            )}
        </Shell>
    );
}

/* ------------------------------------------------- coming back from PayPal */

/**
 * Where PayPal's return_url lands.
 *
 * THIS IS A FALLBACK, NOT THE NORMAL PATH. The SDK keeps approval inside the
 * page, so almost everybody is finished before they could get here. A
 * redirect happens when somebody opens a checkout link directly, or when the
 * popup is blocked - and until 29 Oct 2026 there was no return_url at all,
 * so that case left the buyer on a spinner for ever while their payment went
 * through perfectly well. Landing somewhere that says what happened is the
 * whole point of it.
 *
 * It reads the account rather than trusting the query string: ?token= is a
 * PayPal order id, not proof of anything.
 */
export function PaymentComplete() {
    useStaticSeo({
        title: "Payment complete | Doorstep Data",
        canonical: PRODUCT_ORIGIN + "/account/payment-complete",
        description: "Your payment went through.",
        noindex: true,
    });

    const session = currentSession();
    const [state, setState] = useState({ loading: true, credits: null, plan: null });

    useEffect(() => {
        let alive = true;
        if (!session) {
            setState({ loading: false, credits: null, plan: null });
            return undefined;
        }
        fetchMe(session.token)
            .then((m) => alive && setState({ loading: false, credits: m.credits, plan: m.plan }))
            .catch(() => alive && setState({ loading: false, credits: null, plan: null }));
        return () => {
            alive = false;
        };
    }, [session]);

    return (
        <Shell>
            <Head>Thank you</Head>
            {state.loading && <Blurb>Checking your account…</Blurb>}

            {!state.loading && state.credits !== null && (
                <>
                    <Done>
                        {state.plan && state.plan !== "none"
                            ? "Your plan is active."
                            : state.credits === 1
                            ? "You have 1 report to use."
                            : "You have " + state.credits + " reports to use."}
                    </Done>
                    <Blurb>
                        A report is used the first time you open a property, and opening the same
                        one again is always free.
                    </Blurb>
                </>
            )}

            {!state.loading && state.credits === null && (
                <Blurb>
                    If you have just paid, sign in and your account will show what you bought.
                    Nothing is lost.
                </Blurb>
            )}

            <Alt>
                <Link to="/property-report">Look a property up</Link> ·{" "}
                <Link to="/account">Your account</Link>
            </Alt>
        </Shell>
    );
}

/** Where PayPal sends somebody who backed out. Nothing has been charged. */
export function PaymentCancelled() {
    useStaticSeo({
        title: "Payment cancelled | Doorstep Data",
        canonical: PRODUCT_ORIGIN + "/account/payment-cancelled",
        description: "Nothing has been charged.",
        noindex: true,
    });

    return (
        <Shell>
            <Head>Payment cancelled</Head>
            <Blurb>Nothing has been charged, and nothing has changed on your account.</Blurb>
            <Alt>
                <Link to="/account/buy">Try again</Link> ·{" "}
                <Link to="/property-report">Look a property up</Link>
            </Alt>
        </Shell>
    );
}
